/**
 * The mockup's eligibility vocabulary (grants.md:312 — eleven short labels,
 * alphabetical) as display groups over the 17 grants.gov labels the load
 * procedure writes into `Content.Metadata.eligibility[]` (grantFacets.ts
 * GRANT_ELIGIBILITY_LABELS). Display only: the wire never changes. A group is
 * selected when any of its wire values is; picking it sends the union.
 * Ruling R-P3 folds housing authorities and special districts into Local govt.
 */
export interface EligibilityGroup { readonly Short: string; readonly Wire: readonly string[]; }

export const GRANT_ELIGIBILITY_SHORT_LABELS: readonly EligibilityGroup[] = [
    { Short: 'For-profit', Wire: ['For-profit organizations'] },
    { Short: 'Individuals', Wire: ['Individuals'] },
    { Short: 'Local govt', Wire: ['County governments', 'City or township governments', 'Special district governments', 'Public or Indian housing authorities'] },
    { Short: 'Nonprofits', Wire: ['Nonprofits with 501(c)(3) status', 'Nonprofits without 501(c)(3) status'] },
    { Short: 'Open to all', Wire: ['Unrestricted'] },
    { Short: 'Others', Wire: ['Others'] },
    { Short: 'Schools', Wire: ['Independent school districts'] },
    { Short: 'Small business', Wire: ['Small businesses'] },
    { Short: 'State govt', Wire: ['State governments'] },
    { Short: 'Tribal', Wire: ['Native American tribal governments', 'Native American tribal organizations'] },
    { Short: 'Universities', Wire: ['Public institutions of higher education', 'Private institutions of higher education'] },
];

const SHORT_BY_WIRE: ReadonlyMap<string, string> = new Map(GRANT_ELIGIBILITY_SHORT_LABELS.flatMap((g) => g.Wire.map((w) => [w, g.Short] as const)));

/** A grant's wire values → its short labels, in table order, deduped; a value the table does not know passes through verbatim, after the known ones. */
export function ShortEligibilityLabels(wire: readonly string[]): string[] {
    const known = GRANT_ELIGIBILITY_SHORT_LABELS.filter((g) => g.Wire.some((w) => wire.includes(w))).map((g) => g.Short);
    const unknown = wire.filter((w) => !SHORT_BY_WIRE.has(w));
    return [...known, ...unknown];
}

const WIRE_BY_SHORT: ReadonlyMap<string, readonly string[]> = new Map(GRANT_ELIGIBILITY_SHORT_LABELS.map((g) => [g.Short, g.Wire] as const));

/** Selected short labels → the union of their wire values, in the order `shorts` names them. */
export function WireEligibilityValues(shorts: readonly string[]): string[] {
    return shorts.flatMap((short) => [...(WIRE_BY_SHORT.get(short) ?? [])]);
}

/** Which short labels a wire selection lights up. */
export function SelectedShortEligibility(wire: readonly string[]): string[] {
    return GRANT_ELIGIBILITY_SHORT_LABELS.filter((g) => g.Wire.some((w) => wire.includes(w))).map((g) => g.Short);
}
